/**
 * i18n resources for the Script Studio feature slice.
 *
 * Namespace: `scripts`. Exposed as `{ namespace, resources: { en, bg, de, fr } }`
 * so the app shell can merge it into the shared i18next instance and tests can
 * pass it straight to `renderWithProviders({ i18nResources })`.
 *
 * Key parity is enforced by `@hostesa/i18n` (a missing key THROWS in test +
 * production), so every locale below carries the same key set. Non-`en` locales
 * are seeded as English placeholders where a real translation is not yet
 * available — the documented temporary state.
 */

const en = {
  eyebrow: 'Voice',
  studio: {
    title: 'Script Studio',
    description:
      'Write, review, and approve what the AI says on your behalf — one set of scripts per language.',
    languageLabel: 'Language',
    fallbackLabel: 'Fallback language',
    fallbackHint: 'Calls in a language without approved scripts use this language instead.',
    fallbackError: 'Could not change the fallback language. Please try again.',
    fieldsHeading: 'Script fields',
    requiredLegend: 'Required for live calls',
    notLive: 'Not live',
    live: 'Live',
  },
  fields: {
    greeting: 'Greeting',
    ai_disclosure: 'AI disclosure',
    recording_notice: 'Recording notice',
    reservation_questions: 'Reservation questions',
    no_availability: 'No availability',
    price_unknown: 'Price unknown',
    existing_booking: 'Existing booking',
    human_handoff: 'Human handoff',
    complaint: 'Complaint',
    emergency: 'Emergency',
    call_closing: 'Call closing',
  },
  fieldStatus: {
    approved: 'Approved',
    draft: 'Draft',
    notSet: 'Not set',
  },
  editor: {
    title: 'Editor',
    selectPrompt: 'Select a script field to edit.',
    contentLabel: 'Script text',
    contentPlaceholder: 'Type what the AI should say…',
    charCount: '{{count}} characters',
    charWarning: 'Long scripts can sound unnatural on a phone call. Consider shortening.',
    contentRequired: 'Script text is required.',
    saveDraft: 'Save draft',
    saving: 'Saving…',
    saved: 'Draft saved.',
    saveError: 'Could not save the draft. Please try again.',
    submitForApproval: 'Submit for approval',
    approve: 'Approve',
    approving: 'Approving…',
    approved: 'Script approved.',
    approveError: 'Could not approve the script. Please try again.',
    approvedBy: 'Approved by {{name}}',
    approvedUnknown: 'Approved',
    liveWarning: 'This script is currently live. Changes will not take effect until re-approved.',
    notApprovedNotice: 'Saved drafts are not live. Only approved scripts reach the AI.',
  },
  required: {
    bannerTitle: 'Your hotel is missing required scripts',
    bannerDescription: 'The AI cannot handle live calls until the required scripts are approved.',
  },
  permissions: {
    approveDisabledHint: 'You do not have permission to approve scripts for this hotel.',
  },
  states: {
    loading: 'Loading scripts…',
    errorTitle: 'Could not load scripts',
    errorDescription: 'There was a problem loading the script configuration. Please try again.',
    retry: 'Retry',
    emptyTitle: 'No scripts yet',
    emptyDescription: 'No scripts have been written for this language. Start with the greeting.',
  },
  handoff: {
    title: 'Handoff rules',
    description:
      'Control when a live call is transferred to your reception or staff instead of being handled by the AI.',
    eyebrow: 'Voice',
    transferTargetLabel: 'Transfer target (phone)',
    transferTargetPlaceholder: '+359888000000',
    transferTargetInvalid: 'Enter a valid phone number in international format.',
    maxWaitLabel: 'Max wait before transfer (seconds)',
    maxWaitPlaceholder: '30',
    maxWaitInvalid: 'Enter a number between 0 and 600.',
    transferOnFrustrationLabel: 'Transfer when the guest is frustrated',
    transferOnPaymentLabel: 'Transfer for payment requests',
    save: 'Save handoff rules',
    saving: 'Saving…',
    saved: 'Handoff rules saved.',
    saveError: 'Could not save handoff rules. Please try again.',
    loading: 'Loading handoff rules…',
    errorTitle: 'Could not load handoff rules',
    errorDescription: 'There was a problem loading the voice configuration. Please try again.',
  },
};

type ScriptsResources = typeof en;

// Non-English locales reuse the English copy until translated (key parity).
const bg: ScriptsResources = en;
const de: ScriptsResources = en;
const fr: ScriptsResources = en;

export const namespace = 'scripts' as const;

export const resources: Record<'en' | 'bg' | 'de' | 'fr', { scripts: ScriptsResources }> = {
  en: { scripts: en },
  bg: { scripts: bg },
  de: { scripts: de },
  fr: { scripts: fr },
};

export const scriptsI18n = { namespace, resources } as const;
